import { useState, forwardRef } from 'react';
import { Headphones } from 'lucide-react';
import { useAudio } from '@/context/AudioContext';
import { AudioPlayerDropdown } from './AudioPlayerDropdown';

export const HeaderAudioIcon = forwardRef<HTMLButtonElement | null>(
  function HeaderAudioIcon(_props, ref) {
    const { currentTrack, isPlaying, isLoading, queue } = useAudio();
    const [showDropdown, setShowDropdown] = useState(false);

    // Enable the button if there's a current track OR there are tracks in the queue
    // This allows users to access their playlists and queue even if nothing is actively playing
    const hasContent = !!currentTrack || queue.length > 0;
    const isActive = isPlaying || isLoading;
    const hasContentButtonStyle = isActive
      ? 'bg-[#f8e8f8] text-[#8e4585]'
      : 'text-[#8e4585] hover:bg-[#f8e8f8]';
    const noDropdownButtonStyle = hasContent
      ? hasContentButtonStyle
      : 'text-[#8e4585]/60 hover:bg-[#f8e8f8] hover:text-[#8e4585]';

    return (
      <div className="relative">
        <button
          ref={ref}
          onClick={() => setShowDropdown(!showDropdown)}
          className={`p-2 rounded-lg transition-all duration-200 group relative ${
            showDropdown ? 'bg-[#f8e8f8] text-[#8e4585]' : noDropdownButtonStyle
          }`}
          title={hasContent ? 'Audio Player' : 'Audio Player & Playlists'}
        >
          {/* Icon with animation when playing */}
          <div className={`relative ${isActive ? 'animate-pulse' : ''}`}>
            <Headphones size={20} />

            {/* Sound wave animation indicator */}
            {isActive && (
              <div className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 flex gap-[2px]">
                <span
                  className="w-[2px] h-[6px] bg-[#8e4585] rounded-full animate-bounce"
                  style={{ animationDelay: '0ms', animationDuration: '400ms' }}
                />
                <span
                  className="w-[2px] h-[8px] bg-[#8e4585] rounded-full animate-bounce"
                  style={{ animationDelay: '100ms', animationDuration: '400ms' }}
                />
                <span
                  className="w-[2px] h-[5px] bg-[#8e4585] rounded-full animate-bounce"
                  style={{ animationDelay: '200ms', animationDuration: '400ms' }}
                />
              </div>
            )}
          </div>

          {/* Active indicator dot - shows when there's content available */}
          {hasContent && (
            <span
              className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                isActive ? 'bg-green-500 animate-pulse' : 'bg-[#8e4585]'
              }`}
            />
          )}
        </button>

        {/* Audio Player Dropdown */}
        {showDropdown && <AudioPlayerDropdown onClose={() => setShowDropdown(false)} />}
      </div>
    );
  }
);
